import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link, useLocation } from 'react-router-dom';
import axios from 'axios';
import { ArrowRightLeft, QrCode, Clock, CheckCircle, XCircle, AlertTriangle, ScanLine, LogIn } from 'lucide-react';
import { useAuth, API } from '../App';
import { t } from '../i18n';
import Header from '../components/Header';
import { toast } from 'sonner';

const TransferPage = () => {
  const { token } = useParams();
  const { user } = useAuth();
  const location = useLocation();
  const [transfer, setTransfer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [step, setStep] = useState('info'); // info -> scan -> confirming -> done
  const [scannedPingmeId, setScannedPingmeId] = useState('');
  const [confirming, setConfirming] = useState(false);
  const [remaining, setRemaining] = useState(0);

  // Persist the transfer URL so the user lands back here after Google OAuth
  // (which redirects via Emergent and loses React Router state).
  useEffect(() => {
    if (!user) {
      try { sessionStorage.setItem('post_login_redirect', location.pathname); } catch (_) {}
    }
  }, [user, location.pathname]);

  const fetchTransfer = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/transfer/${token}`);
      setTransfer(res.data);
      setRemaining(res.data.remaining_seconds || 0);
      if (res.data.status === 'confirmed') setStep('done');
      else if (res.data.status === 'expired' || res.data.status === 'cancelled') setStep('expired');
    } catch {
      setTransfer({ status: 'not_found' });
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { fetchTransfer(); }, [fetchTransfer]);

  // Countdown timer
  useEffect(() => {
    if (remaining <= 0 || step === 'done' || step === 'expired') return;
    const timer = setInterval(() => {
      setRemaining(prev => {
        if (prev <= 1) {
          clearInterval(timer);
          setStep('expired');
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(timer);
  }, [remaining, step]);

  const confirmTransfer = async () => {
    if (!scannedPingmeId.trim()) {
      toast.error('Scannez ou entrez l\'ID du PingMe original');
      return;
    }
    setConfirming(true);
    try {
      const res = await axios.post(`${API}/transfer/${token}/confirm`, {
        pingme_id: scannedPingmeId.trim()
      }, { withCredentials: true });
      toast.success(`PingMe "${res.data.pingme_name}" transfere !`);
      setStep('done');
      setTransfer(prev => ({ ...prev, status: 'confirmed', pingme_name: res.data.pingme_name }));
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur de transfert');
    } finally {
      setConfirming(false);
    }
  };

  const formatTime = (s) => {
    const m = Math.floor(s / 60);
    const sec = s % 60;
    return `${m}:${sec.toString().padStart(2, '0')}`;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="flex items-center justify-center py-20">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-primary border-t-transparent"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="px-6 sm:px-8 lg:px-12 py-12">
        <div className="max-w-lg mx-auto">

          {/* Not found */}
          {transfer?.status === 'not_found' && (
            <div className="card-brutal text-center p-8">
              <XCircle className="w-16 h-16 text-danger mx-auto mb-4" />
              <h1 className="text-2xl font-bold font-heading text-charcoal mb-2">Transfert introuvable</h1>
              <p className="text-charcoal/60">Ce lien de transfert n'existe pas ou a ete supprime.</p>
              <Link to="/" className="btn-primary mt-6 inline-block">Retour a l'accueil</Link>
            </div>
          )}

          {/* Expired */}
          {(step === 'expired' || transfer?.status === 'expired' || transfer?.status === 'cancelled') && (
            <div className="card-brutal text-center p-8">
              <Clock className="w-16 h-16 text-charcoal/30 mx-auto mb-4" />
              <h1 className="text-2xl font-bold font-heading text-charcoal mb-2">Transfert expire</h1>
              <p className="text-charcoal/60">Ce lien de transfert a expire ou a ete annule. Demandez au proprietaire d'en generer un nouveau.</p>
              <Link to="/" className="btn-primary mt-6 inline-block">Retour a l'accueil</Link>
            </div>
          )}

          {/* Transfer done */}
          {step === 'done' && (
            <div className="card-brutal text-center p-8">
              <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
              <h1 className="text-2xl font-bold font-heading text-charcoal mb-2">Transfert reussi !</h1>
              <p className="text-charcoal/60 mb-2">Le PingMe <strong>"{transfer?.pingme_name}"</strong> vous appartient maintenant.</p>
              <p className="text-sm text-charcoal/40">Il apparait dans votre Dashboard.</p>
              <Link to="/dashboard" className="btn-primary mt-6 inline-block" data-testid="transfer-go-dashboard">
                Voir mon Dashboard
              </Link>
            </div>
          )}

          {/* Active transfer - need login */}
          {transfer?.status === 'pending' && step !== 'expired' && step !== 'done' && !user && (
            <div className="card-brutal text-center p-8">
              <ArrowRightLeft className="w-16 h-16 text-primary mx-auto mb-4" />
              <h1 className="text-2xl font-bold font-heading text-charcoal mb-2">Transfert de PingMe</h1>
              <p className="text-charcoal/60 mb-2">
                <strong>{transfer.from_user_name}</strong> souhaite vous transferer le PingMe <strong>"{transfer.pingme_name}"</strong>
              </p>
              <div className="flex items-center justify-center gap-2 text-sm text-charcoal/50 mb-6">
                <Clock className="w-4 h-4" />
                <span>Expire dans {formatTime(remaining)}</span>
              </div>
              <div className="bg-accent/30 border-2 border-charcoal/10 rounded-xl p-4 mb-6">
                <LogIn className="w-8 h-8 text-charcoal mx-auto mb-2" />
                <p className="text-sm font-bold text-charcoal">Connectez-vous pour accepter le transfert</p>
                <p className="text-xs text-charcoal/60 mt-1">Vous reviendrez automatiquement ici apres connexion.</p>
              </div>
              <div className="flex flex-col sm:flex-row gap-2 justify-center">
                <Link
                  to="/login"
                  state={{ from: location }}
                  className="btn-primary inline-block"
                  data-testid="transfer-login-btn"
                >
                  {t('nav.login')}
                </Link>
                <Link
                  to="/register"
                  state={{ from: location }}
                  className="btn-outline inline-block"
                  data-testid="transfer-register-btn"
                >
                  Creer un compte
                </Link>
              </div>
            </div>
          )}

          {/* Active transfer - logged in, step: info */}
          {transfer?.status === 'pending' && step === 'info' && user && (
            <div className="card-brutal p-8">
              <div className="text-center mb-8">
                <ArrowRightLeft className="w-16 h-16 text-primary mx-auto mb-4" />
                <h1 className="text-2xl font-bold font-heading text-charcoal mb-2">Transfert de PingMe</h1>
                <p className="text-charcoal/60">
                  <strong>{transfer.from_user_name}</strong> souhaite vous transferer :
                </p>
                <div className="bg-primary/10 border-2 border-primary rounded-xl p-4 mt-4">
                  <p className="text-xl font-bold text-charcoal font-heading">"{transfer.pingme_name}"</p>
                </div>
              </div>

              <div className="flex items-center justify-center gap-2 text-sm text-charcoal/50 mb-6">
                <Clock className="w-4 h-4" />
                <span>Expire dans <strong>{formatTime(remaining)}</strong></span>
              </div>

              <div className="bg-surface-alt rounded-xl p-4 mb-6">
                <div className="flex items-start gap-3">
                  <AlertTriangle className="w-5 h-5 text-charcoal/50 shrink-0 mt-0.5" />
                  <div className="text-sm text-charcoal/70">
                    <p className="font-bold mb-1">Etape suivante :</p>
                    <p>Vous devez scanner le <strong>QR code original</strong> du PingMe pour prouver que vous avez l'objet en main.</p>
                  </div>
                </div>
              </div>

              <button
                onClick={() => setStep('scan')}
                className="w-full btn-primary flex items-center justify-center gap-2"
                data-testid="transfer-proceed-btn"
              >
                <ScanLine className="w-5 h-5" />
                Scanner le PingMe original
              </button>
            </div>
          )}

          {/* Active transfer - logged in, step: scan */}
          {transfer?.status === 'pending' && step === 'scan' && user && (
            <div className="card-brutal p-8">
              <div className="text-center mb-6">
                <ScanLine className="w-12 h-12 text-primary mx-auto mb-3" />
                <h2 className="text-xl font-bold font-heading text-charcoal mb-2">Scanner le PingMe original</h2>
                <p className="text-sm text-charcoal/60">
                  Scannez le QR code du PingMe "<strong>{transfer.pingme_name}</strong>" ou entrez son identifiant
                </p>
              </div>

              <div className="flex items-center justify-center gap-2 text-sm text-charcoal/50 mb-6">
                <Clock className="w-4 h-4" />
                <span>{formatTime(remaining)}</span>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="text-xs font-bold tracking-[0.1em] uppercase text-charcoal mb-2 block">
                    Identifiant PingMe
                  </label>
                  <input
                    type="text"
                    value={scannedPingmeId}
                    onChange={(e) => setScannedPingmeId(e.target.value)}
                    className="input-brutal w-full"
                    placeholder="ping_xxxxxxxxxxxx"
                    data-testid="transfer-pingme-id-input"
                  />
                  <p className="text-xs text-charcoal/40 mt-1">
                    L'identifiant se trouve dans l'URL apres avoir scanne le QR : /ping/<strong>ping_xxx</strong>
                  </p>
                </div>

                <button
                  onClick={confirmTransfer}
                  disabled={confirming || !scannedPingmeId.trim()}
                  className="w-full btn-primary flex items-center justify-center gap-2 disabled:opacity-50"
                  data-testid="transfer-confirm-btn"
                >
                  <CheckCircle className="w-5 h-5" />
                  {confirming ? 'Transfert en cours...' : 'Confirmer le transfert'}
                </button>

                <button
                  onClick={() => setStep('info')}
                  className="w-full btn-outline text-sm"
                >
                  {t('actions.back')}
                </button>
              </div>
            </div>
          )}

        </div>
      </main>
    </div>
  );
};

export default TransferPage;
